import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "./contexts/AuthContext";

import Landing from "./pages/Landing";
import Verify from "./pages/Verify";
import Login from "./pages/Login";
import NewsFeed from "./pages/NewsFeed";
import HashtagPage from "./pages/HashtagPage";
import Profile from "./pages/Profile";
import ProfileEdit from "./pages/ProfileEdit";
import NotificationSettings from "./pages/NotificationSettings";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminBadges from "./pages/AdminBadges";
import AdminTickets from "./pages/AdminTickets";
import AdminPanel from "./pages/AdminPanel";
import ManageOrg from "./pages/ManageOrg";
import ToolsPage from "./pages/ToolsPage";
import { TicketsList, TicketDetail } from "./pages/TicketsPages";
import ChatPage from "./pages/ChatPage";
import SettingsPage from "./pages/SettingsPage";
import GroupsPage from "./pages/GroupsPage";
import GroupDetailPage from "./pages/GroupDetailPage";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import Donate from "./pages/Donate";
import SavedPosts from "./pages/SavedPosts";
import ForgotPassword from "./pages/ForgotPassword";
import { About, NotFound } from "./pages/AboutAndErrors";
import { Terms, Privacy } from "./pages/StaticPages";

class ErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error("App crashed:", err, info); }
  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen bg-[#050d1a] text-white flex flex-col items-center justify-center text-center px-4" data-testid="error-boundary-page">
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-red-400">// 500</div>
          <h1 className="text-5xl font-bold mt-2">Something went sideways</h1>
          <p className="text-[#94a3b8] mt-2 max-w-md">Our security team has been notified. Try reloading — most issues clear up on retry.</p>
          <button onClick={() => window.location.reload()} className="csc-btn-primary mt-6" data-testid="error-reload-btn">Reload</button>
          <a href="/home" className="csc-btn-ghost mt-3" data-testid="error-home-btn">Back home</a>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-[#050d1a] flex items-center justify-center text-[#94a3b8] font-mono-tech text-xs uppercase tracking-[0.32em]" data-testid="loading-screen">
        <div className="flex items-center gap-3">
          <span className="inline-block w-3 h-3 rounded-full bg-[#39ff14] animate-pulse" />
          Initializing secure session…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/home" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (user) return <Navigate to="/home" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/verify" element={<Verify />} />

      <Route path="/home" element={<ProtectedRoute><NewsFeed /></ProtectedRoute>} />
      <Route path="/saved" element={<ProtectedRoute><SavedPosts /></ProtectedRoute>} />
      <Route path="/tag/:tag" element={<ProtectedRoute><HashtagPage /></ProtectedRoute>} />

      <Route path="/profile/me" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
      <Route path="/profile/:id" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/u/:handle" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/org/:id" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/org/:orgId/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
      <Route path="/org/manage" element={<ProtectedRoute><ManageOrg /></ProtectedRoute>} />

      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />

      <Route path="/tools" element={<ProtectedRoute><ToolsPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/tickets" element={<ProtectedRoute><TicketsList /></ProtectedRoute>} />
      <Route path="/tickets/:number" element={<ProtectedRoute><TicketDetail /></ProtectedRoute>} />

      <Route path="/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/groups/:id" element={<ProtectedRoute><GroupDetailPage /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
      <Route path="/events/:id" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />

      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/badges" element={<ProtectedRoute adminOnly><AdminBadges /></ProtectedRoute>} />
      <Route path="/admin/tickets" element={<ProtectedRoute adminOnly><AdminTickets /></ProtectedRoute>} />

      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<About />} />
      <Route path="/donate" element={<ProtectedRoute><Donate /></ProtectedRoute>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster
              theme="dark"
              position="top-right"
              duration={4000}
              toastOptions={{
                style: {
                  background: "#0d1b2e",
                  border: "1px solid rgba(0,170,255,0.18)",
                  color: "#ffffff",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: "12px",
                },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
